/**
 * /zh/comparisons/notion-ai — Chinese version of the Notion AI
 * comparison. Cloned from NotionAi.tsx; content translated native
 * (not MT). Same visual structure, same MarketingShell, same routing
 * shape — App.tsx switches on `/zh/comparisons/notion-ai` path.
 *
 * MarketingShell auto-emits hreflang link tags so Google knows this
 * URL is the zh alternate of /comparisons/notion-ai.
 */
import { MarketingShell } from '../MarketingShell';

export function NotionAiZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/comparisons/notion-ai"
      documentTitle="面向 AI 产品工作的 Notion 替代方案：Draftlize vs Notion AI · Draftlize"
      documentDescription="在找为 AI 产品工作打造的 Notion 替代品？Notion AI 只是贴在笔记上的聊天按钮；Draftlize 是 AI agent 能读也能写的卡片图，让 PM 的决策不漂移。并排对比。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">对比 · vs Notion AI</span>
          <h1
            className="serif"
            style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}
          >
            Draftlize vs Notion AI：<br/>
            <em>哪个适合你的 PM 工作流？</em>
          </h1>
          <p className="lede">
            一句话：Notion AI 是贴在笔记上的聊天助手。Draftlize 是 AI agent 能把你的产品思考作为结构化卡片读、也能写的 substrate。解决相邻问题；不是一个替代另一个，而是要先想清楚你到底需要哪个。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">逐项对比，<em>7 个维度。</em></h2>
          </div>
          <p>来自 6 周 PM dogfood 数据 + 同 roadmap、同场景在两个工具上并行跑测出来的结果。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Notion AI</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Substrate 形状</td><td className="ours yes">类型化卡片图</td><td className="partial">页面 + block</td></tr>
            <tr><td>AI 跨对话自动把决策作为 context 读</td><td className="ours yes">原生</td><td className="no">—</td></tr>
            <tr><td>AI 能把发现以结构化卡片形式写回</td><td className="ours yes">原生</td><td className="partial">写 block 但无结构</td></tr>
            <tr><td>依赖变化时 stale 传播</td><td className="ours yes">原生</td><td className="no">—</td></tr>
            <tr><td>跨 thread / 跨页面冲突检测</td><td className="ours yes">原生</td><td className="no">—</td></tr>
            <tr><td>自由格式富文本、嵌入、数据库</td><td className="no">—</td><td className="yes">出色</td></tr>
            <tr><td>团队 wiki / 协作 / 权限</td><td className="no">仅单人（v1）</td><td className="yes">出色</td></tr>
            <tr><td>Claude Code / MCP / 一等 AI 工具集成</td><td className="ours yes">原生</td><td className="no">—</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Notion AI <em>赢的地方。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">如果你要 wiki</h3>
            <p>Notion 是存在的最好的 wiki 工具。如果你的问题是"团队的事故响应 runbook 放哪"，选 Notion。Draftlize 不试图做 wiki。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">如果你住在表格 + 数据库里</h3>
            <p>Notion 的数据库 / 视图体系无可匹敌。roadmap 跟踪表、sprint 规划网格、供应商评估矩阵——这些形状 Notion 的结构化集合永远赢卡片。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">如果你团队 &gt;5 人</h3>
            <p>实时协作、细粒度权限、评论、@提及——Notion 是为它建的。Draftlize 设计上是单人优先。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Draftlize <em>赢的地方。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">AI agent <em>真的</em>能记住</h3>
            <p>Notion AI 的"记忆"是把内容尽力检索喂进 chat context。你 @-mention 一个页面 agent 才能读。在 Draftlize 里，agent 每次对话自动读 <code>c-04</code>（你的定价决策），因为卡片就是 agent 工作的 substrate。重复解释降到零。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Spec 漂移变得可见</h3>
            <p>改一个 Notion 页面——什么都不会发生。改一个 Draftlize 决策卡——所有依赖卡自动标记 <code>stale: true</code>。substrate 像构建系统追踪失效构件那样追踪失效。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Claude Code / Cursor / MCP 的一等 context</h3>
            <p>Notion AI 住在 Notion 里。Draftlize 卡片通过 CLI 落到你的 repo，通过 MCP server 在 Claude Code / Cursor / Claude Desktop 里浮现，还有 4 个 slash command。决策从任何 AI 工具里都可寻址——不止一个。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Notion 回答"团队的东西写在哪"。Draftlize 回答"AI agent 干一半活的时候，产品思考在哪里复利"。
        </blockquote>
        <cite>如果你只需要前一个答案，Notion 就够了。</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">用 $5 <em>免费开始。</em></h2>
        </div>
        <div className="trust-strip">
          <span>注册送 $5</span>
          <span>按量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            注册送 $5，按量付费，余额永不过期。你可以继续用 Notion。Draftlize 是 AI 辅助那部分下面的 substrate。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
